import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { HOME_CONTENT_MOCK } from '../data/home-content.mock';
import type {
  HomeAliadosContent,
  HomeAliadosLogoBannerRow,
  HomeAliadosLogosResponse,
  HomeAliadosTextBannerRow,
  HomeAliadosTextosResponse,
} from '../models/home-aliados-content.model';
import {
  mapAliadosLogoBanners,
  mapAliadosTextBannersToContent,
} from '../utils/home-aliados-banner.mapper';
import { prepareAliadosTitleHtml } from '../utils/home-aliados-html.util';

/** Debe coincidir con `NeoBannerDTO.ALIADOS_FINANCIEROS` */
export const ALIADOS_TEXTOS_BANNER_TIPO = 'AliadosFinancieros';

/** Debe coincidir con `NeoBannerDTO.TIPO_ALIADO_FINANCIERO` */
export const ALIADOS_LOGOS_BANNER_TIPO = 'Financiero';

@Injectable({
  providedIn: 'root',
})
export class HomeAliadosService {
  private readonly fallbackContent: HomeAliadosContent = buildAliadosFallbackFromMock();

  constructor(private readonly bancoldexService: BancoldexService) {}

  loadContent(): Observable<HomeAliadosContent> {
    return forkJoin({
      textos: this.bancoldexService
        .getArchivosBannerActivos(ALIADOS_TEXTOS_BANNER_TIPO)
        .pipe(catchError(() => of({ exitoso: false, banners: [] } as HomeAliadosTextosResponse))),
      logos: this.bancoldexService
        .getArchivosBannerOrderByNameAliados(ALIADOS_LOGOS_BANNER_TIPO)
        .pipe(catchError(() => of({ exitoso: false, banners: [] } as HomeAliadosLogosResponse))),
    }).pipe(
      map(({ textos, logos }) => {
        const textRows = normalizeAliadosTextRows(textos as HomeAliadosTextosResponse);
        const logoRows = normalizeAliadosLogoRows(logos as HomeAliadosLogosResponse);
        const textContent = mapAliadosTextBannersToContent(textRows, this.fallbackContent);

        return {
          ...textContent,
          logos: mapAliadosLogoBanners(logoRows),
        };
      }),
      catchError(() => of({ ...this.fallbackContent, logos: [] }))
    );
  }
}

function buildAliadosFallbackFromMock(): HomeAliadosContent {
  const mock = HOME_CONTENT_MOCK.aliados;
  return {
    eyebrow: mock.eyebrow,
    titleHtml: prepareAliadosTitleHtml(mock.titleHtml),
    description: mock.description,
    joinPrompt: mock.joinPrompt,
    uniteLabel: mock.uniteLabel,
    uniteUrl: mock.uniteUrl,
    logos: [],
  };
}

function normalizeAliadosTextRows(
  response: HomeAliadosTextosResponse
): HomeAliadosTextBannerRow[] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }

  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as unknown as HomeAliadosTextBannerRow[][]).flat();
  }

  return response.banners;
}

function normalizeAliadosLogoRows(
  response: HomeAliadosLogosResponse
): HomeAliadosLogoBannerRow[] {
  if (!response?.exitoso || !response.banners?.length) {
    return [];
  }

  const first = response.banners[0] as unknown;
  if (Array.isArray(first)) {
    return (response.banners as unknown as HomeAliadosLogoBannerRow[][]).flat();
  }

  return response.banners as HomeAliadosLogoBannerRow[];
}
